import {
  AggregateBusinessFilterProperty,
  FetchRequiredFilterType,
  ForecastTimelineFilterProperty,
} from '@/coreDomainsModule/entities/filter';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { UnmanagedBusinessService } from '@/coreDomainsModule/services/unmanagedBusinessService';
import { ICustomerForecastRepository } from '@/coreInfrastructureModule/interfaces/ICustomerForecastRepository';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { IFilterInfoRepository, IFilterInfoResponse } from '@/coreInfrastructureModule/interfaces/IFilterInfoRepository';
import { IUnmanagedBusinessRepository } from '@/coreInfrastructureModule/interfaces/IUnmanagedBusinessRepository';
import { FilterModalType } from '@/corePagesModule/filterModal/filterModal';
import { CustomerForecastFilterItemsAdapter } from '../adapter/customerForecastFilterItemsAdapter';
import { i18n } from '@/i18n';
import { daysTocalculateDate } from '@/appLogic/business/businessLogic';
import { simpleCopy } from '@/utils/deepCopy';
import { UNASSIGNED_COLUMN } from '@/coreDomainsModule/constants';
import { ForecastForReceiptRepository } from '@/coreInfrastructureModule/Repositories/ForecastForReceiptRepository';
import { IForecastForReceiptRepository } from '@/coreInfrastructureModule/interfaces/IForecastForReceiptManage';
import { IUnmatchedOrderRepository } from '@/coreInfrastructureModule/interfaces/IUnmatchedOrderRepository';

/**
 *  「発注情報」を取ってきてUI表示ように加工するUseCase
 */
export class GetFilterInfoUseCase {
  private defaultPagingOptions: IPagingOptions = initPagingOptions(30);

  private filterInfoRepository: IFilterInfoRepository;
  private customerForecastRepository: ICustomerForecastRepository;
  private unmanagedBusinessRepository: IUnmanagedBusinessRepository;
  private forecastForReceiptRepository: IForecastForReceiptRepository;
  private unmatchedOrderRepository: IUnmatchedOrderRepository;

  /**
   * コンストラクタ
   * @param filterInfoRepository - 取引情報の永続化の責務を負うクラス
   * @param customerForecastRepository - 客先内示の永続化処理を行うクラス
   * @param unmanagedBusinessRepository - 未管理の取引の永続化処理を行うクラス
   * @param forecastForReceiptRepository - 受領用内示の永続化の責務を担うクラス
   * @param unmatchedOrderRepository - 未照合差異発注の永続化の責務を負うクラス
   */
  constructor(
    filterInfoRepository: IFilterInfoRepository,
    customerForecastRepository: ICustomerForecastRepository,
    unmanagedBusinessRepository: IUnmanagedBusinessRepository,
    forecastForReceiptRepository: ForecastForReceiptRepository,
    unmatchedOrderRepository: IUnmatchedOrderRepository
  ) {
    this.filterInfoRepository = filterInfoRepository;
    this.customerForecastRepository = customerForecastRepository;
    this.unmanagedBusinessRepository = unmanagedBusinessRepository;
    this.forecastForReceiptRepository = forecastForReceiptRepository;
    this.unmatchedOrderRepository = unmatchedOrderRepository;
  }

  /**
   * TableDataをBusinessManagerから抽出する
   * @param filterKey - フィルターキー
   * @param keyword - 検索キーワード
   * @param filterModalType - フィルターモーダルの種類
   * @param aggregateBusinessFilterProperty - フィルター情報(集計取引用)
   * @param customerForecastFilterProperty - フィルター情報(客先内示用)
   * @param pagingOptions - ページングオプション
   */
  public Execute = async (
    filterKey: FetchRequiredFilterType,
    keyword = '',
    filterModalType: FilterModalType,
    aggregateBusinessFilterProperty?: AggregateBusinessFilterProperty,
    customerForecastFilterProperty?: ForecastTimelineFilterProperty,
    pagingOptions?: IPagingOptions
  ): Promise<INetworkResult<IFilterInfoResponse, IError>> => {
    try {
      const usePagingOptions = pagingOptions || this.defaultPagingOptions;
      let result: INetworkResult<IFilterInfoResponse, IError>;
      const leadTimeInfo =
        aggregateBusinessFilterProperty?.leadTime && aggregateBusinessFilterProperty?.leadTimeUnit
          ? {
              value: aggregateBusinessFilterProperty?.leadTime,
              unit: aggregateBusinessFilterProperty.leadTimeUnit,
            }
          : undefined;
      const stdInventoryIndicesMinInfo =
        aggregateBusinessFilterProperty?.stdInventoryIndicesMin && aggregateBusinessFilterProperty?.stdInventoryIndicesUnit
          ? {
              value: aggregateBusinessFilterProperty?.stdInventoryIndicesMin,
              unit: aggregateBusinessFilterProperty.stdInventoryIndicesUnit,
            }
          : undefined;
      const stdInventoryIndicesMaxInfo =
        aggregateBusinessFilterProperty?.stdInventoryIndicesMax && aggregateBusinessFilterProperty?.stdInventoryIndicesUnit
          ? {
              value: aggregateBusinessFilterProperty?.stdInventoryIndicesMax,
              unit: aggregateBusinessFilterProperty.stdInventoryIndicesUnit,
            }
          : undefined;
      switch (filterKey) {
        case 'SUPPLIER_NAME':
          result = await this.filterInfoRepository.getSupplierNameList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'SUPPLIER_CODE':
          result = await this.filterInfoRepository.getSupplierCodeList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'WAREHOUSE_CODE':
          result = await this.filterInfoRepository.getWarehouseCodeList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'WAREHOUSE':
          result = await this.filterInfoRepository.getWarehouseList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'BUSINESS_CODE':
          result = await this.filterInfoRepository.getBusinessCodeList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'BUSINESS_NAME':
          result = await this.filterInfoRepository.getBusinessNameList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'PACKAGE_AMOUNT':
          result = await this.filterInfoRepository.getPackageAmountList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'UNIT':
          result = await this.filterInfoRepository.getUnitList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'MANUFACTURER':
          result = await this.filterInfoRepository.getManufacturerList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'LEAD_TIME':
          result = await this.filterInfoRepository.getLeadTimeList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'STANDARD_INVENTORY_INDEX_MIN':
          result = await this.filterInfoRepository.getStdInventoryIndicesMinList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'STANDARD_INVENTORY_INDEX_MAX':
          result = await this.filterInfoRepository.getStdInventoryIndicesMaxList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'MOQ':
          result = await this.filterInfoRepository.getMOQList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'SUPPLIER_FORECAST_RESPONSIBLE_USER':
          result = await this.filterInfoRepository.getSupplierForecastResponsibleUserList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'AGGREGATE_BUSINESS_GROUP_FILTER_INFO':
          result = await this.filterInfoRepository.gerAggregateBusinessGroupFilterInfo(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.supplierForecastResponsibleUserId,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'ORDER_PACKAGE':
          result = await this.filterInfoRepository.getOrderPackageList(
            usePagingOptions,
            aggregateBusinessFilterProperty?.supplierCode,
            aggregateBusinessFilterProperty?.supplierName,
            aggregateBusinessFilterProperty?.businessCode,
            aggregateBusinessFilterProperty?.businessName,
            aggregateBusinessFilterProperty?.packageAmount,
            aggregateBusinessFilterProperty?.warehouseCode,
            aggregateBusinessFilterProperty?.warehouse,
            aggregateBusinessFilterProperty?.unit,
            aggregateBusinessFilterProperty?.manufacturer,
            leadTimeInfo,
            stdInventoryIndicesMinInfo,
            stdInventoryIndicesMaxInfo,
            aggregateBusinessFilterProperty?.MOQ,
            aggregateBusinessFilterProperty?.orderPackage,
            keyword
          );
          break;
        case 'CUSTOMER_CODE':
          result = await this.filterInfoRepository.getCustomerCodeList(
            usePagingOptions,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            keyword
          );
          break;
        case 'CUSTOMER_NAME':
          result = await this.filterInfoRepository.getCustomerNameList(
            usePagingOptions,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            keyword
          );
          break;
        case 'SALES_DESTINATION_CODE':
          result = await this.filterInfoRepository.getSalesDestinationCodeList(
            usePagingOptions,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            keyword
          );
          break;
        case 'SALES_DESTINATION_NAME':
          result = await this.filterInfoRepository.getSalesDestinationNameList(
            usePagingOptions,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            keyword
          );
          break;
        case 'END_USER':
          result = await this.filterInfoRepository.getEndUserList(
            usePagingOptions,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            keyword
          );
          break;
        case 'CUSTOMER_FORECAST_RESPONSIBLE_USER':
          result = await this.filterInfoRepository.getCustomerForecastResponsibleUserList(
            usePagingOptions,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            keyword
          );
          break;
        case 'FORECAST_FOR_RECEIPT_NAME': {
          const data = await this.forecastForReceiptRepository.getForecastForReceipt();
          if (data.status.type === 'error') throw new Error();
          const dataList = data.status.data.data.map((d) => d.forecastForReceiptName);
          result = {
            status: {
              type: 'success',
              data: {
                data: dataList,
                pagingOptions: {
                  pageSize: dataList.length,
                  offset: dataList.length,
                  returnCount: dataList.length,
                },
              },
            },
          };
          break;
        }
        case 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER':
          result = await this.filterInfoRepository.getForecastForReceiptResponsibleUserList(
            usePagingOptions,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            keyword
          );
          break;
        case 'STAFF_NAME':
          result = await this.filterInfoRepository.getStaffNameList(usePagingOptions);
          break;
        case 'ORDER_ID':
          result = await this.filterInfoRepository.getOrderIDList(usePagingOptions, keyword);
          break;
        case 'ORDER_DATE':
          result = await this.filterInfoRepository.getOrderDateList(usePagingOptions);
          break;
        case 'DESIRED_DELIVERY_DATE':
          result = await this.filterInfoRepository.getOrderDesiredDeliveryDateList(usePagingOptions);
          break;
        case 'ORDER_NUMBER':
          result = await this.filterInfoRepository.getOrderNumberList(usePagingOptions, keyword);
          break;
        case 'DELIVERY_DATE':
          result = await this.filterInfoRepository.getOrderDeliveryDateList(usePagingOptions);
          break;
        case 'ORDER_AMOUNT':
          result = await this.filterInfoRepository.getOrderAmountList(usePagingOptions, keyword);
          break;
        case 'ORDER_GROUP_NAME':
          result = await this.filterInfoRepository.getOrderGroupNameList(usePagingOptions, undefined, undefined, keyword);
          break;
        case 'INVOICE_NUMBER':
          result = await this.filterInfoRepository.getInvoiceNumberList(usePagingOptions, undefined, undefined, keyword);
          break;
        case 'DESTINATION_NAME':
          result = await this.filterInfoRepository.getDestinationNameList(usePagingOptions, undefined, undefined, keyword);
          break;
        case 'UNMANAGED_BUSINESS_ARRIVAL_AMOUNT':
        case 'UNMANAGED_BUSINESS_SUPPLIER_NUMBER':
        case 'UNMANAGED_BUSINESS_SUPPLIER_NAME':
        case 'UNMANAGED_BUSINESS_BUSINESS_NUMBER':
        case 'UNMANAGED_BUSINESS_BUSINESS_NAME':
        case 'UNMANAGED_BUSINESS_PACKAGE':
        case 'UNMANAGED_BUSINESS_WAREHOUSE_NUMBER':
        case 'UNMANAGED_BUSINESS_WAREHOUSE':
        case 'UNMANAGED_BUSINESS_INVENTORY_AMOUNT':
        case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-1':
        case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-2':
        case 'UNMANAGED_BUSINESS_ANNOUNCE_AMOUNT-3':
        case 'UNMANAGED_BUSINESS_CUSTOMER_NUMBER':
        case 'UNMANAGED_BUSINESS_CUSTOMER_NAME':
        case 'UNMANAGED_BUSINESS_SALES_DESTINATION_NUMBER':
        case 'UNMANAGED_BUSINESS_SALES_DESTINATION_NAME':
        case 'UNMANAGED_BUSINESS_SALES_RESULTS_AMOUNT':
        case 'UNMANAGED_BUSINESS_END_USER':
        case 'UNMANAGED_BUSINESS_MANUFACTURER':
          result = this.unmanagedBusinessRepository.getFilter(
            usePagingOptions,
            filterKey,
            UnmanagedBusinessService.convertFilterModalTypeToImportFileType(filterModalType),
            keyword
          );
          break;

        case 'FORECAST_TIMELINE_STAFF_NAME':
          result = await this.customerForecastRepository.getStaffNameFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_WAREHOUSE_CODE':
          result = await this.customerForecastRepository.getWarehouseCodeFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_WAREHOUSE':
          result = await this.customerForecastRepository.getWarehouseFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_SUPPLIER_CODE':
          result = await this.customerForecastRepository.getSupplierCodeFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_SUPPLIER_NAME':
          result = await this.customerForecastRepository.getSupplierNameFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_BUSINESS_CODE':
          result = await this.customerForecastRepository.getBusinessCodeFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_BUSINESS_NAME':
          result = await this.customerForecastRepository.getBusinessNameFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_PACKAGE_AMOUNT':
          result = await this.customerForecastRepository.getPackageAmountFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_CUSTOMER_CODE':
          result = await this.customerForecastRepository.getCustomerCodeFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_CUSTOMER_NAME':
          result = await this.customerForecastRepository.getCustomerNameFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_SALES_DESTINATION_CODE':
          result = await this.customerForecastRepository.getSalesDestinationCodeFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;
        case 'FORECAST_TIMELINE_SALES_DESTINATION_NAME':
          result = await this.customerForecastRepository.getSalesDestinationNameFilterItems(
            CustomerForecastFilterItemsAdapter.adapt(customerForecastFilterProperty),
            keyword
          );
          break;

        case 'UNMATCHED_SUPPLIER_NUMBER':
          result = await this.unmatchedOrderRepository.getFilter('supplierNumber', keyword);
          break;
        case 'UNMATCHED_SUPPLIER_NAME':
          result = await this.unmatchedOrderRepository.getFilter('supplierName', keyword);
          break;
        case 'UNMATCHED_BUSINESS_NUMBER':
          result = await this.unmatchedOrderRepository.getFilter('businessNumber', keyword);
          break;
        case 'UNMATCHED_BUSINESS_NAME':
          result = await this.unmatchedOrderRepository.getFilter('businessName', keyword);
          break;
        case 'UNMATCHED_WAREHOUSE_NUMBER':
          result = await this.unmatchedOrderRepository.getFilter('warehouseNumber', keyword);
          break;
        case 'UNMATCHED_WAREHOUSE':
          result = await this.unmatchedOrderRepository.getFilter('warehouse', keyword);
          break;
        case 'UNMATCHED_ORDER_NUMBER':
          result = await this.unmatchedOrderRepository.getFilter('orderNumber', keyword);
          break;
        case 'UNMATCHED_ORDER_ISSUED_NUMBER':
          result = await this.unmatchedOrderRepository.getFilter('orderIssuedNumber', keyword);
          break;
        default:
          throw Error('想定外のエラー');
      }

      switch (result.status.type) {
        case 'success':
          // 結果を降順にソートする
          // any型を使用しているため、typeofで型を確認する
          if (result.status.data.data && result.status.data.data.length > 0) {
            if (
              [
                'MANUFACTURER',
                'END_USER',
                'ORDER_PACKAGE',
                'ORDER_NUMBER',
                'ORDER_GROUP_NAME',
                'INVOICE_NUMBER',
                'DESTINATION_NAME',
                'UNMATCHED_SUPPLIER_NUMBER',
                'UNMATCHED_SUPPLIER_NAME',
                'UNMATCHED_BUSINESS_NUMBER',
                'UNMATCHED_BUSINESS_NAME',
                'UNMATCHED_WAREHOUSE_NUMBER',
                'UNMATCHED_WAREHOUSE',
                'UNMATCHED_ORDER_NUMBER',
                'UNMATCHED_ORDER_ISSUED_NUMBER',
              ].includes(filterKey)
            ) {
              const filteredResult = result.status.data.data.filterNotNull();
              const hasNull = result.status.data.data.length !== filteredResult.length;
              const collator = new Intl.Collator(i18n.locale, { numeric: true });
              result.status.data.data = simpleCopy(
                filteredResult.sort((a: string, b: string) => {
                  return collator.compare(b, a);
                })
              );
              if (hasNull) {
                result.status.data.data.unshift(UNASSIGNED_COLUMN);
              }
            } else if (filterKey === 'LEAD_TIME') {
              if (result.status.data.data[0].hasOwnProperty('value')) {
                result.status.data.data.sort((a, b) => {
                  return b.value - a.value;
                });
              }
              result.status.data.data = result.status.data.data.map(
                (leadTimeInfo) => String(daysTocalculateDate(leadTimeInfo.value, leadTimeInfo.unit).displayData) + leadTimeInfo.unit
              );
            } else if (filterKey === 'STANDARD_INVENTORY_INDEX_MIN' || filterKey === 'STANDARD_INVENTORY_INDEX_MAX') {
              if (result.status.data.data[0].hasOwnProperty('value')) {
                result.status.data.data.sort((a, b) => {
                  return b.value - a.value;
                });
              }
              result.status.data.data = result.status.data.data.map(
                (standardInventoryIndexInfo) => String(standardInventoryIndexInfo.value) + standardInventoryIndexInfo.unit
              );
            } else if (
              filterKey === 'SUPPLIER_FORECAST_RESPONSIBLE_USER' ||
              filterKey === 'CUSTOMER_FORECAST_RESPONSIBLE_USER' ||
              filterKey === 'FORECAST_TIMELINE_STAFF_NAME' ||
              filterKey === 'FORECAST_FOR_RECEIPT_RESPONSIBLE_USER'
            ) {
              // nullが返却される場合があるため、null以外をフィルターする
              const filteredResult = result.status.data.data.filterNotNull();
              // 返却値とnullを除いた値を比較してnullを持っているかを確認する
              const hasNull = result.status.data.data.length !== filteredResult.length;

              if (!filteredResult.isEmpty()) {
                const collator = new Intl.Collator(i18n.locale, { numeric: true });
                filteredResult.sort((a, b) => {
                  const userNameA = a.name === '' ? a.id : a.name;
                  const userNameB = b.name === '' ? b.id : b.name;
                  return collator.compare(userNameB, userNameA);
                });
              }
              result.status.data.data = simpleCopy(
                filteredResult.map((userInfo) => {
                  const userName = userInfo.name === '' ? userInfo.id : userInfo.name;
                  return `${userName},${userInfo.id}`;
                })
              );
              // nullがある場合は先頭に UNASSIGNED_COLUMN を追加する
              if (hasNull) {
                result.status.data.data.unshift(UNASSIGNED_COLUMN);
              }
            } else if (filterKey === 'AGGREGATE_BUSINESS_GROUP_FILTER_INFO') {
              // nullが返却される場合があるため、null以外をフィルターする
              const filteredResult = result.status.data.data.filterNotNull();
              // 返却値とnullを除いた値を比較してnullを持っているかを確認する
              const hasNull = result.status.data.data.length !== filteredResult.length;
              if (!filteredResult.isEmpty() && filteredResult[0].hasOwnProperty('name')) {
                const collator = new Intl.Collator(i18n.locale, { numeric: true });
                filteredResult.sort((a, b) => {
                  return collator.compare(b.name, a.name);
                });
              }
              result.status.data.data = simpleCopy(
                filteredResult.map((orderGroupInfo) => {
                  return `${orderGroupInfo.name},${orderGroupInfo.id}`;
                })
              );
              // nullがある場合は先頭に UNASSIGNED_COLUMN を追加する
              if (hasNull) {
                result.status.data.data.unshift(UNASSIGNED_COLUMN);
              }
            } else if (typeof result.status.data.data[0] === 'number') {
              result.status.data.data.sort((a: number, b: number) => {
                return b - a;
              });
            } else if (typeof result.status.data.data[0] === 'string') {
              const collator = new Intl.Collator(i18n.locale, { numeric: true });
              result.status.data.data.sort((a: string, b: string) => {
                return collator.compare(b, a);
              });
            } else {
              result.status.data.data.sort().reverse();
            }
          }

          console.log(result.status.data);

          return {
            status: {
              type: 'success',
              data: result.status.data,
            },
          };
        case 'error':
          console.error(result.status.error);
          return {
            status: {
              type: 'error',
              error: result.status.error,
            },
          };
        default:
          throw Error('想定外のエラー');
      }
    } catch (error) {
      console.error('error', error); // 基本的にはここには来ない
      return {
        status: {
          type: 'error',
          error: {
            statusCode: 500,
            code: 'unknown',
            message: 'An unexpected error occurred',
          },
        },
      };
    }
  };
}
